import { Alert, Badge, Button, EmptyState, FieldBlock, Input, LoadingState, Select, Textarea, useToast } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { FormRow, FormScreen, FormSection } from '../../components/FormScreen.tsx'
import { CamposDaEntidade } from './CamposDaEntidade.tsx'
import { api } from '../../lib/api.ts'
import { useBootstrap } from '../../lib/catalog.ts'
import { SwitchCard, normalizarCodigo } from './catalog-shared.tsx'
import { useConfirm } from '../work-item/bits.tsx'

/**
 * Cadastro de Empresas (SPEC-20260815-0821), padrao FormScreen. Razao social e
 * dado (nao traduzivel); os dominios de e-mail sao cadastrais por ora — o
 * modulo de e-mail que correlaciona mensagem -> empresa e futuro.
 */

type ClientList = Awaited<ReturnType<Awaited<ReturnType<(typeof api.catalog)['companies']['$get']>>['json']>>
type CompanyRow = ClientList['companies'][number]

type FormEmpresa = {
  name: string
  code: string
  /** Texto livre separado por virgula; a API normaliza e deduplica. */
  domains: string
  ownerMemberId: string | null
  /** Contatos da empresa (2026-08-25). */
  phone: string
  email: string
  website: string
  notes: string
  isActive: boolean
  /** PESSOAS vinculadas (N:N) com a FUNCAO de cada uma nesta empresa. */
  people: { personId: string; role: string }[]
  /** Campos complementares dos TOPICOS de Empresa (fase 6). */
  customValues: Record<string, unknown>
}

const VAZIO: FormEmpresa = {
  name: '',
  code: '',
  domains: '',
  ownerMemberId: null,
  phone: '',
  email: '',
  website: '',
  notes: '',
  isActive: true,
  people: [],
  customValues: {},
}

const SEM_RESPONSAVEL = 'SEM_RESPONSAVEL'

/**
 * Telefone brasileiro com máscara na EXIBIÇÃO (o cadastro guarda como veio):
 * (61) 99125-1737 / (61) 3212-1737 / +55 (61) 99125-1737.
 */
function telefoneFormatado(valor: string): string {
  const so = valor.replace(/\D/g, '')
  const comPais = so.length > 11 && so.startsWith('55')
  const n = comPais ? so.slice(2) : so
  const pais = comPais ? '+55 ' : ''
  if (n.length === 11) return `${pais}(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`
  if (n.length === 10) return `${pais}(${n.slice(0, 2)}) ${n.slice(2, 6)}-${n.slice(6)}`
  if (n.length === 9) return `${n.slice(0, 5)}-${n.slice(5)}`
  if (n.length === 8) return `${n.slice(0, 4)}-${n.slice(4)}`
  // fora do padrão brasileiro: mostra como está, sem inventar formato
  return valor
}

/** Conversa no WhatsApp: só dígitos, com o 55 do Brasil quando falta o país. */
function linkWhatsapp(telefone: string): string {
  const so = telefone.replace(/\D/g, '')
  const comPais = so.length > 11 ? so : `55${so}`
  return `https://wa.me/${comPais}`
}

/** Escrever no Gmail (web), já com o destinatário preenchido. */
function linkGmail(email: string): string {
  return `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}`
}

export function CompaniesSection({ criarSinal = 0 }: { criarSinal?: number }) {
  const toast = useToast()
  const qc = useQueryClient()
  const bootstrap = useBootstrap()
  const navigate = useNavigate()
  const confirm = useConfirm()

  const lista = useQuery({
    queryKey: ['companies'],
    queryFn: async () => (await api.catalog.companies.$get()).json(),
  })
  // PESSOAS do workspace: opcoes do vinculo N:N (funcao junto)
  const pessoas = useQuery({
    queryKey: ['people'],
    queryFn: async () => (await api.catalog.people.$get()).json(),
  })

  const [busca, setBusca] = useState('')
  const [editando, setEditando] = useState<{ id: string | null } | null>(null)
  const [form, setForm] = useState<FormEmpresa>(VAZIO)
  const [confirmandoExclusao, setConfirmandoExclusao] = useState<string | null>(null)
  // empresas expandidas na lista (mostra as pessoas: nome · cargo, telefone, e-mail)
  const [expandidas, setExpandidas] = useState<Set<string>>(new Set())
  const alternarExpandir = (id: string) =>
    setExpandidas((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  // secao Pessoas da empresa (2026-08-25): busca por nome ao adicionar,
  // criacao inline de pessoa nova e edicao do CARD gravando direto no
  // cadastro da pessoa
  const [pessoaBusca, setPessoaBusca] = useState<string | null>(null)
  const [pessoaEmEdicao, setPessoaEmEdicao] = useState<string | null>(null)
  const [cardDraft, setCardDraft] = useState({ name: '', email: '', phone: '', role: '' })

  useEffect(() => {
    if (criarSinal > 0) abrir(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criarSinal])

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['companies'] })
    void qc.invalidateQueries({ queryKey: ['bootstrap'] })
  }

  const aoFalhar = (erro: unknown) =>
    toast.push({
      tone: 'danger',
      message: erro instanceof Error && erro.message ? erro.message : 'Não foi possível concluir. Tente novamente.',
    })

  /** "a, b" -> [a, b] (varios contatos, 2026-08-25). */
  const listaDe = (texto: string) =>
    texto.split(',').map((t) => t.trim()).filter(Boolean)

  const dominiosDe = (texto: string) =>
    texto
      .split(',')
      .map((d) => d.trim().toLowerCase().replace(/^@/, ''))
      .filter((d) => d.length >= 3)

  const corpoDe = (f: FormEmpresa) => ({
    name: f.name.trim(),
    code: normalizarCodigo(f.code || f.name),
    emailDomains: dominiosDe(f.domains),
    ownerMemberId: f.ownerMemberId,
    phones: listaDe(f.phone),
    emails: listaDe(f.email),
    website: f.website.trim() || null,
    notes: f.notes.trim() || null,
    isActive: f.isActive,
    customValues: f.customValues,
  })

  /** Grava o vinculo N:N pessoa<->empresa com a funcao. */
  const salvarPessoas = async (companyId: string, f: FormEmpresa) => {
    await api.catalog.companies[':id'].people.$put({
      param: { id: companyId },
      json: { links: f.people.filter((l) => l.personId !== '').map((l) => ({ personId: l.personId, role: l.role.trim() || null })) },
    })
  }

  /** Cria a pessoa DIGITADA na busca direto no cadastro de Pessoas e ja a
   *  vincula, abrindo o card em edicao para completar os contatos. */
  const criarPessoaInline = useMutation({
    mutationFn: async (nome: string) => (await api.catalog.people.$post({ json: { name: nome.trim() } })).json(),
    onSuccess: (dados) => {
      void qc.invalidateQueries({ queryKey: ['people'] })
      if (!dados.person) return
      setForm((f) => ({ ...f, people: [...f.people, { personId: dados.person!.id, role: '' }] }))
      setPessoaBusca(null)
      setPessoaEmEdicao(dados.person.id)
      setCardDraft({ name: dados.person.name, email: '', phone: '', role: '' })
      toast.push({ tone: 'success', message: `“${dados.person.name}” entrou no cadastro de Pessoas.` })
    },
    onError: aoFalhar,
  })

  /** Salva o CARD: nome/contatos vao DIRETO para o cadastro da pessoa; a
   *  funcao fica no vinculo (persistida ja, se a empresa existe). */
  const salvarCardPessoa = useMutation({
    mutationFn: async ({ personId }: { personId: string }) => {
      await api.catalog.people[':id'].$patch({
        param: { id: personId },
        json: {
          name: cardDraft.name.trim(),
          emails: listaDe(cardDraft.email),
          phones: listaDe(cardDraft.phone),
        },
      })
      const linksNovos = form.people.map((l) => (l.personId === personId ? { ...l, role: cardDraft.role } : l))
      setForm((f) => ({ ...f, people: f.people.map((l) => (l.personId === personId ? { ...l, role: cardDraft.role } : l)) }))
      if (editando?.id) {
        await api.catalog.companies[':id'].people.$put({
          param: { id: editando.id },
          json: {
            links: linksNovos.filter((l) => l.personId !== '').map((l) => ({ personId: l.personId, role: l.role.trim() || null })),
          },
        })
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['people'] })
      void qc.invalidateQueries({ queryKey: ['companies'] })
      setPessoaEmEdicao(null)
      toast.push({ tone: 'success', message: 'Dados da pessoa salvos no cadastro.' })
    },
    onError: aoFalhar,
  })

  const criar = useMutation({
    mutationFn: async (f: FormEmpresa) => {
      const criado = await (await api.catalog.companies.$post({ json: corpoDe(f) })).json()
      if (criado.company && f.people.length > 0) await salvarPessoas(criado.company.id, f)
      return criado
    },
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Empresa criada.' })
    },
    onError: aoFalhar,
  })

  const atualizar = useMutation({
    mutationFn: async ({ id, f }: { id: string; f: FormEmpresa }) => {
      const salvo = await (await api.catalog.companies[':id'].$patch({ param: { id }, json: corpoDe(f) })).json()
      await salvarPessoas(id, f)
      return salvo
    },
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Empresa atualizada.' })
    },
    onError: aoFalhar,
  })

  const alternarAtivo = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      (await api.catalog.companies[':id'].active.$post({ param: { id }, json: { isActive } })).json(),
    onSuccess: invalidar,
    onError: aoFalhar,
  })

  // ativar/desativar e excluir a PESSOA direto da lista da empresa (2026-08-29)
  const alternarAtivoPessoa = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      (await api.catalog.people[':id'].active.$post({ param: { id }, json: { isActive } })).json(),
    onSuccess: invalidar,
    onError: aoFalhar,
  })
  const excluirPessoa = useMutation({
    mutationFn: async (id: string) => (await api.catalog.people[':id'].$delete({ param: { id } })).json(),
    onSuccess: () => {
      invalidar()
      toast.push({ tone: 'success', message: 'Pessoa excluída.' })
    },
    onError: aoFalhar,
  })

  const excluir = useMutation({
    mutationFn: async (id: string) => (await api.catalog.companies[':id'].$delete({ param: { id } })).json(),
    onSuccess: () => {
      invalidar()
      setConfirmandoExclusao(null)
      toast.push({ tone: 'success', message: 'Empresa excluído.' })
    },
    onError: (erro) => {
      setConfirmandoExclusao(null)
      aoFalhar(erro)
    },
  })

  const companies = lista.data?.companies ?? []
  const emEdicao = editando?.id ? companies.find((c) => c.id === editando.id) : undefined
  const membros = (bootstrap.data?.members ?? []).filter((c) => c.isActive)
  const nomeDoResponsavel = (id: string | null) =>
    id ? (bootstrap.data?.members ?? []).find((c) => c.id === id)?.name ?? null : null

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    if (termo === '') return companies
    return companies.filter((c) =>
      `${c.name} ${c.code} ${c.emailDomains.join(' ')}`.toLowerCase().includes(termo),
    )
  }, [companies, busca])

  const formDe = (alvo: CompanyRow): FormEmpresa => ({
    name: alvo.name,
    code: alvo.code,
    domains: alvo.emailDomains.join(', '),
    ownerMemberId: alvo.ownerMemberId,
    phone: alvo.phones.join(', '),
    email: alvo.emails.join(', '),
    website: alvo.website ?? '',
    notes: alvo.notes ?? '',
    isActive: alvo.isActive,
    people: alvo.people.map((p) => ({ personId: p.personId, role: p.role ?? '' })),
    customValues: ((alvo as { customValues?: Record<string, unknown> }).customValues ?? {}),
  })

  const abrir = (alvo: CompanyRow | null) => {
    setForm(alvo ? formDe(alvo) : VAZIO)
    setEditando({ id: alvo?.id ?? null })
  }

  const assinatura = (f: FormEmpresa) =>
    JSON.stringify([corpoDe(f), f.people.filter((l) => l.personId !== '').map((l) => `${l.personId}:${l.role.trim()}`)])
  const alterado =
    editando?.id == null
      ? form.name.trim().length >= 2
      : emEdicao !== undefined && assinatura(form) !== assinatura(formDe(emEdicao))

  // ------------------------------------------------------- formulário ---

  if (editando !== null) {
    const siglaDerivada = normalizarCodigo(form.code || form.name)
    const salvarValido = alterado && form.name.trim().length >= 2 && !criar.isPending && !atualizar.isPending

    return (
      <FormScreen
        title={editando.id ? 'Editar empresa' : 'Nova empresa'}
        subtitle={editando.id ? emEdicao?.name : undefined}
        onClose={() => setEditando(null)}
        dirty={alterado}
        dirtyHint={editando.id ? 'Tudo salvo' : 'Preencha a razão social para salvar'}
        saveLabel={editando.id ? 'Salvar' : 'Criar empresa'}
        saveDisabled={!salvarValido}
        saving={criar.isPending || atualizar.isPending}
        onSubmit={() => {
          if (!salvarValido) return
          if (editando.id) atualizar.mutate({ id: editando.id, f: form })
          else criar.mutate(form)
        }}
      >
        <FormSection title="Informações do cliente">
          <FormRow cols="2fr 1fr 1fr">
            <FieldBlock label="Razão social" required>
              {({ id }) => (
                <Input
                  id={id}
                  autoFocus
                  value={form.name}
                  placeholder="Ex.: Banco Meridiano S.A."
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              )}
            </FieldBlock>
            <FieldBlock
              label="Código"
              hint={
                form.code.trim() === '' && form.name.trim() !== ''
                  ? `Derivado do nome: ${siglaDerivada}. Digite para personalizar.`
                  : 'Identificador técnico, único no workspace.'
              }
            >
              {({ id }) => (
                <Input
                  id={id}
                  value={form.code}
                  placeholder={siglaDerivada || 'Ex.: MERID'}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                  onBlur={() => setForm((f) => ({ ...f, code: f.code ? normalizarCodigo(f.code) : f.code }))}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Responsável interno" hint="Quem responde por este cliente no time.">
              {({ id }) => (
                <Select<string>
                  id={id}
                  value={form.ownerMemberId ?? SEM_RESPONSAVEL}
                  options={[
                    { value: SEM_RESPONSAVEL, label: 'Sem responsável' },
                    ...membros.map((c) => ({ value: c.id, label: c.name })),
                  ]}
                  onChange={(valor) =>
                    setForm((f) => ({ ...f, ownerMemberId: valor === SEM_RESPONSAVEL ? null : valor }))
                  }
                />
              )}
            </FieldBlock>
          </FormRow>

          <FormRow cols="1fr">
            <FieldBlock
              label="Domínios de e-mail"
              hint="Separe por vírgula — ex.: meridiano.com.br, bancomeridiano.com. É a base para correlacionar e-mails recebidos ao cliente."
            >
              {({ id }) => (
                <Input
                  id={id}
                  value={form.domains}
                  placeholder="meridiano.com.br, bancomeridiano.com"
                  onChange={(e) => setForm((f) => ({ ...f, domains: e.target.value }))}
                />
              )}
            </FieldBlock>
          </FormRow>

          <FormRow cols="1fr 1fr 1fr">
            <FieldBlock label="Telefones" hint="Separe por vírgula.">
              {({ id }) => (
                <Input
                  id={id}
                  value={form.phone}
                  placeholder="(61) 99999-0000, (61) 3333-0000"
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                />
              )}
            </FieldBlock>
            <FieldBlock label="E-mails" hint="Separe por vírgula.">
              {({ id }) => (
                <Input
                  id={id}
                  value={form.email}
                  placeholder="contato@empresa.com.br, comercial@empresa.com.br"
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Site">
              {({ id }) => (
                <Input
                  id={id}
                  value={form.website}
                  placeholder="https://empresa.com.br"
                  onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
                />
              )}
            </FieldBlock>
          </FormRow>

          <FormRow cols="1fr">
            <FieldBlock label="Observações" hint="Contexto do contrato, contatos-chave…">
              {({ id }) => (
                <Textarea
                  id={id}
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                />
              )}
            </FieldBlock>
          </FormRow>

          <FormRow cols="minmax(220px, 420px)">
            <SwitchCard
              title={form.isActive ? 'Empresa ativo' : 'Empresa inativo'}
              description={form.isActive ? 'Aparece nas opções de Demandas, itens e quadros.' : 'Some das opções; registros existentes ficam.'}
              checked={form.isActive}
              onChange={(isActive) => setForm((f) => ({ ...f, isActive }))}
            />
          </FormRow>

          {emEdicao ? (
            <p className="adm-hint">
              <i className="fas fa-link" aria-hidden />{' '}
              {emEdicao.usageCount > 0
                ? `Em uso por ${emEdicao.usageCount} ${emEdicao.usageCount === 1 ? 'registro' : 'registros'} (itens, demandas e quadros).`
                : 'Ainda sem uso.'}
            </p>
          ) : null}
        </FormSection>

        {/* Topicos de EMPRESA (fase 6): cada topico vira uma secao com os
            campos colocados nele. */}
        <CamposDaEntidade
          entity="company"
          valores={form.customValues}
          onMudar={(customValues) => setForm((f) => ({ ...f, customValues }))}
        />

        {/* PESSOAS da empresa (N:N, 2026-08-25): CARDS espelhando o cadastro
            de Pessoas (nome/telefone/e-mail) + a FUNCAO desta empresa (do
            vinculo). Editar grava DIRETO no cadastro da pessoa; a busca por
            nome cria a pessoa na hora quando o nome nao existe. */}
        <FormSection title={`Pessoas${form.people.length > 0 ? ` (${form.people.length})` : ''}`}>
          {form.people
            .filter((l) => l.personId !== '')
            .map((vinculo) => {
              const pessoa = (pessoas.data?.people ?? []).find((p) => p.id === vinculo.personId)
              const emEdicaoDoCard = pessoaEmEdicao === vinculo.personId
              if (emEdicaoDoCard) {
                return (
                  <FormRow key={vinculo.personId} cols="2fr 1fr 1fr 1fr auto">
                    <FieldBlock label="Nome" required>
                      {({ id }) => (
                        <Input id={id} autoFocus value={cardDraft.name}
                          onChange={(e) => setCardDraft((d) => ({ ...d, name: e.target.value }))} />
                      )}
                    </FieldBlock>
                    <FieldBlock label="Telefones">
                      {({ id }) => (
                        <Input id={id} value={cardDraft.phone} placeholder="(61) 99999-0000, …"
                          onChange={(e) => setCardDraft((d) => ({ ...d, phone: e.target.value }))} />
                      )}
                    </FieldBlock>
                    <FieldBlock label="E-mails">
                      {({ id }) => (
                        <Input id={id} value={cardDraft.email} placeholder="pessoa@empresa.com.br, …"
                          onChange={(e) => setCardDraft((d) => ({ ...d, email: e.target.value }))} />
                      )}
                    </FieldBlock>
                    <FieldBlock label="Função na empresa">
                      {({ id }) => (
                        <Input id={id} value={cardDraft.role} placeholder="Ex.: Gerente de TI"
                          onChange={(e) => setCardDraft((d) => ({ ...d, role: e.target.value }))} />
                      )}
                    </FieldBlock>
                    <FieldBlock label=" ">
                      {() => (
                        <span style={{ display: 'inline-flex', gap: 6 }}>
                          <Button variant="primary" loading={salvarCardPessoa.isPending}
                            disabled={cardDraft.name.trim().length < 2}
                            onClick={() => salvarCardPessoa.mutate({ personId: vinculo.personId })}>
                            Salvar
                          </Button>
                          <Button variant="ghost" onClick={() => setPessoaEmEdicao(null)}>Cancelar</Button>
                        </span>
                      )}
                    </FieldBlock>
                  </FormRow>
                )
              }
              return (
                <div key={vinculo.personId} className="adm-catrow">
                  <span className="adm-catrow__icon"><i className="fas fa-user" aria-hidden /></span>
                  <span className="adm-catrow__main">
                    <span className="adm-catrow__name">
                      {pessoa?.name ?? '…'}
                      {vinculo.role.trim() ? <span style={{ fontWeight: 400, color: 'var(--ob-text-muted)' }}> · {vinculo.role}</span> : null}
                    </span>
                    <span className="adm-catrow__sub">
                      {[...(pessoa?.phones ?? []), ...(pessoa?.emails ?? [])].join(' · ') || 'Sem contato'}
                    </span>
                  </span>
                  <button type="button" className="adm-iconbtn" aria-label={`Editar ${pessoa?.name ?? 'pessoa'}`}
                    title="Editar — grava direto no cadastro da pessoa"
                    onClick={() => {
                      setPessoaEmEdicao(vinculo.personId)
                      setCardDraft({
                        name: pessoa?.name ?? '',
                        email: (pessoa?.emails ?? []).join(', '),
                        phone: (pessoa?.phones ?? []).join(', '),
                        role: vinculo.role,
                      })
                    }}>
                    <i className="fas fa-pen" aria-hidden />
                  </button>
                  <button type="button" className="adm-iconbtn adm-iconbtn--danger"
                    aria-label="Remover pessoa da empresa" title="Remover desta empresa (a pessoa continua no cadastro)"
                    onClick={() => setForm((f) => ({ ...f, people: f.people.filter((l) => l.personId !== vinculo.personId) }))}>
                    <i className="fas fa-trash-alt" aria-hidden />
                  </button>
                </div>
              )
            })}

          {pessoaBusca !== null ? (
            (() => {
              const termo = pessoaBusca.trim().toLowerCase()
              const jaVinculadas = new Set(form.people.map((l) => l.personId))
              const sugestoes = (pessoas.data?.people ?? [])
                .filter((p) => !jaVinculadas.has(p.id))
                .filter((p) => termo === '' || p.name.toLowerCase().includes(termo))
                .slice(0, 6)
              const nomeNovo =
                pessoaBusca.trim().length >= 2 &&
                !(pessoas.data?.people ?? []).some((p) => p.name.toLowerCase() === termo)
              return (
                <FormRow cols="minmax(280px, 460px)">
                  <FieldBlock label="Pessoa" hint="Digite o nome; se não existir, dá para adicionar na hora.">
                    {({ id }) => (
                      <div>
                        <Input id={id} autoFocus value={pessoaBusca} placeholder="Buscar pessoa pelo nome…"
                          onChange={(e) => setPessoaBusca(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Escape') setPessoaBusca(null) }} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                          {sugestoes.map((p) => (
                            <button key={p.id} type="button" className="adm-catrow" style={{ cursor: 'pointer', textAlign: 'left' }}
                              onClick={() => {
                                setForm((f) => ({ ...f, people: [...f.people, { personId: p.id, role: '' }] }))
                                setPessoaBusca(null)
                              }}>
                              <span className="adm-catrow__icon"><i className="fas fa-user" aria-hidden /></span>
                              <span className="adm-catrow__main">
                                <span className="adm-catrow__name">{p.name}</span>
                                <span className="adm-catrow__sub">{[...p.phones, ...p.emails].join(' · ') || 'Sem contato'}</span>
                              </span>
                            </button>
                          ))}
                          {nomeNovo ? (
                            <Button variant="secondary" icon="fas fa-user-plus" loading={criarPessoaInline.isPending}
                              onClick={() => criarPessoaInline.mutate(pessoaBusca)}>
                              Adicionar “{pessoaBusca.trim()}” ao cadastro de Pessoas
                            </Button>
                          ) : null}
                          {sugestoes.length === 0 && !nomeNovo ? (
                            <p className="adm-hint" style={{ margin: 0 }}>
                              <i className="fas fa-info-circle" aria-hidden /> Digite ao menos 2 letras para buscar ou criar.
                            </p>
                          ) : null}
                          <Button variant="ghost" onClick={() => setPessoaBusca(null)}>Cancelar</Button>
                        </div>
                      </div>
                    )}
                  </FieldBlock>
                </FormRow>
              )
            })()
          ) : (
            <FormRow cols="minmax(200px, 300px)">
              <Button variant="secondary" icon="fas fa-user-plus" onClick={() => setPessoaBusca('')}>
                Adicionar pessoa
              </Button>
            </FormRow>
          )}
        </FormSection>
      </FormScreen>
    )
  }

  // ------------------------------------------------------------ lista ---

  const cabecalho = (
    <div className="adm-head">
      <div>
        <h1 className="adm-head__title">Empresas</h1>
        <p className="adm-head__desc">Cadastro com domínios de e-mail correlacionados — a base para ligar demandas à empresa certa.</p>
      </div>
      <Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>
        Incluir
      </Button>
    </div>
  )

  if (lista.isPending) return (<>{cabecalho}<LoadingState variant="list" label="Carregando os empresas" /></>)
  if (lista.isError) {
    return (
      <>
        {cabecalho}
        <div className="adm-placeholder">
          <Alert tone="danger" title="Não foi possível carregar os empresas"
            action={<Button variant="secondary" icon="fas fa-rotate-right" onClick={() => void lista.refetch()}>Tentar de novo</Button>}>
            Verifique a conexão e tente novamente.
          </Alert>
        </div>
      </>
    )
  }

  return (
    <>
      {cabecalho}

      <div className="adm-toolbar">
        <div className="adm-toolbar__search">
          <i className="fas fa-search" aria-hidden />
          <input type="search" placeholder="Filtrar por nome, código ou domínio…" aria-label="Filtrar empresas"
            value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        <span className="adm-toolbar__count">
          {filtrados.length} de {companies.length} {companies.length === 1 ? 'registro' : 'registros'}
        </span>
      </div>

      {filtrados.length === 0 ? (
        <div className="adm-placeholder">
          <EmptyState icon="fas fa-building"
            title={companies.length === 0 ? 'Nenhuma empresa ainda' : 'Nenhuma empresa com esse filtro'}
            description={companies.length === 0
              ? 'Cadastre a primeira empresa para ligar demandas e itens a ele.'
              : `Nada casa com “${busca}”.`}
            action={companies.length === 0
              ? (<Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>Nova empresa</Button>)
              : (<Button variant="secondary" icon="fas fa-times" onClick={() => setBusca('')}>Limpar o filtro</Button>)} />
        </div>
      ) : (
        <div className="adm-list">
          {filtrados.map((row) => {
            const confirmando = confirmandoExclusao === row.id
            const responsavel = nomeDoResponsavel(row.ownerMemberId)
            const aberta = expandidas.has(row.id)
            const temPessoas = row.people.length > 0
            return (
              <div key={row.id} className="adm-catgroup">
              <div className={`adm-catrow${row.isActive ? '' : ' adm-catrow--off'}`}>
                {temPessoas ? (
                  <button
                    type="button"
                    className="adm-expand"
                    aria-expanded={aberta}
                    aria-label={aberta ? `Recolher pessoas de ${row.name}` : `Ver pessoas de ${row.name}`}
                    title={aberta ? 'Recolher' : 'Ver pessoas'}
                    onClick={() => alternarExpandir(row.id)}
                  >
                    <i className={`fas fa-chevron-${aberta ? 'down' : 'right'}`} aria-hidden />
                  </button>
                ) : (
                  <span className="adm-expand adm-expand--vazio" aria-hidden />
                )}
                <span className="adm-catrow__icon"><i className="fas fa-building" aria-hidden /></span>
                <span className="adm-catrow__main">
                  <span className="adm-catrow__name">{row.name}</span>
                  <span className="adm-catrow__sub">
                    Código {row.code}
                    {row.emailDomains.length > 0 ? ` · ${row.emailDomains.join(', ')}` : ''}
                    {responsavel ? ` · ${responsavel}` : ''}
                    {row.people.length > 0 ? ` · ${row.people.length} ${row.people.length === 1 ? 'pessoa' : 'pessoas'}` : ''}
                    {row.usageCount > 0 ? ` · em uso por ${row.usageCount} ${row.usageCount === 1 ? 'registro' : 'registros'}` : ' · sem uso'}
                  </span>
                </span>
                {confirmando ? (
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--ob-danger)' }}>Excluir “{row.name}”?</span>
                    <Button variant="danger" loading={excluir.isPending} onClick={() => excluir.mutate(row.id)}>
                      Confirmar
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirmandoExclusao(null)}>
                      Cancelar
                    </Button>
                  </span>
                ) : (
                  <>
                    <span className="adm-switchwrap">
                      <span className="adm-switchwrap__label">{row.isActive ? 'Ativo' : 'Inativo'}</span>
                      <button type="button" role="switch" aria-checked={row.isActive}
                        aria-label={`${row.name} está ativo`}
                        title={row.isActive ? 'Ativo — clique para desativar' : 'Inativo — clique para ativar'}
                        className={`adm-switch${row.isActive ? ' adm-switch--on' : ''}`}
                        onClick={() => alternarAtivo.mutate({ id: row.id, isActive: !row.isActive })}>
                        <span className="adm-switch__knob" aria-hidden />
                      </button>
                    </span>
                    {/* adicionar pessoa direto na empresa (2026-08-29): abre a
                        empresa em edição, onde as pessoas são vinculadas */}
                    <button type="button" className="adm-iconbtn" aria-label={`Adicionar pessoa em ${row.name}`}
                      title="Adicionar pessoa" onClick={() => abrir(row)}>
                      <i className="fas fa-user-plus" aria-hidden />
                    </button>
                    <button type="button" className="adm-iconbtn" aria-label={`Editar ${row.name}`}
                      title="Editar" onClick={() => abrir(row)}>
                      <i className="fas fa-pen" aria-hidden />
                    </button>
                    <button type="button" className="adm-iconbtn adm-iconbtn--danger" disabled={row.usageCount > 0}
                      aria-label={row.usageCount > 0 ? `${row.code} está em uso e não pode ser excluído` : `Excluir ${row.code}`}
                      title={row.usageCount > 0 ? 'Empresa em uso: desative-o para tirá-lo das opções' : 'Excluir'}
                      onClick={() => setConfirmandoExclusao(row.id)}>
                      <i className="fas fa-trash-alt" aria-hidden />
                    </button>
                  </>
                )}
              </div>
              {aberta && temPessoas ? (
                <div className="adm-people">
                  {row.people.map((p) => {
                    const pessoa = p as typeof p & { isActive?: boolean }
                    const ativa = pessoa.isActive !== false
                    const fones = p.phones ?? []
                    const mails = p.emails ?? []
                    return (
                      <div key={p.personId} className={`adm-personrow${ativa ? '' : ' adm-personrow--off'}`}>
                        <span className="adm-personrow__icon"><i className="fas fa-user" aria-hidden /></span>
                        <span className="adm-personrow__main">
                          <span className="adm-personrow__name">
                            {p.name}
                            {p.role ? <span className="adm-personrow__role">{p.role}</span> : null}
                          </span>
                          {/* telefone abre o WhatsApp e o e-mail abre o Gmail
                              (nova aba) — 2026-08-29 */}
                          <span className="adm-personrow__contatos">
                            {fones.map((f) => (
                              <a
                                key={f}
                                className="adm-personrow__contato adm-personrow__contato--link"
                                href={linkWhatsapp(f)}
                                target="_blank"
                                rel="noreferrer"
                                title={`Conversar com ${p.name} no WhatsApp`}
                              >
                                <i className="fab fa-whatsapp" aria-hidden />
                                {telefoneFormatado(f)}
                              </a>
                            ))}
                            {mails.map((m) => (
                              <a
                                key={m}
                                className="adm-personrow__contato adm-personrow__contato--link"
                                href={linkGmail(m)}
                                target="_blank"
                                rel="noreferrer"
                                title={`Escrever para ${m} no Gmail`}
                              >
                                <i className="fas fa-envelope" aria-hidden />
                                {m}
                              </a>
                            ))}
                            {fones.length === 0 && mails.length === 0 ? (
                              <span className="adm-personrow__contato adm-personrow__contato--vazio">Sem contato</span>
                            ) : null}
                          </span>
                        </span>

                        {/* ativo · editar · excluir da PESSOA (2026-08-29) */}
                        <span className="adm-switchwrap">
                          <span className="adm-switchwrap__label">{ativa ? 'Ativa' : 'Inativa'}</span>
                          <button type="button" role="switch" aria-checked={ativa}
                            aria-label={`${p.name} está ativa`}
                            title={ativa ? 'Ativa — clique para desativar' : 'Inativa — clique para ativar'}
                            className={`adm-switch${ativa ? ' adm-switch--on' : ''}`}
                            onClick={() => alternarAtivoPessoa.mutate({ id: p.personId, isActive: !ativa })}>
                            <span className="adm-switch__knob" aria-hidden />
                          </button>
                        </span>
                        <button type="button" className="adm-iconbtn" aria-label={`Editar ${p.name}`}
                          title="Editar a pessoa" onClick={() => navigate(`/admin/people?editar=${p.personId}`)}>
                          <i className="fas fa-pen" aria-hidden />
                        </button>
                        <button type="button" className="adm-iconbtn adm-iconbtn--danger"
                          aria-label={`Excluir ${p.name}`} title="Excluir a pessoa"
                          onClick={() =>
                            confirm.ask(`A pessoa “${p.name}” será excluída do cadastro e desvinculada das empresas.`, () =>
                              excluirPessoa.mutate(p.personId),
                            )
                          }>
                          <i className="fas fa-trash-alt" aria-hidden />
                        </button>
                      </div>
                    )
                  })}
                </div>
              ) : null}
              </div>
            )
          })}
        </div>
      )}
      {confirm.element}
    </>
  )
}
